import { arrayMoveImmutable } from 'array-move';
import { LabelWithPrefix } from 'components/Select';
import Constants from 'config/Constants';
import { mapValuesDeep } from 'deepdash-es/standalone';
import _ from 'lodash';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import slugify from 'slugify';
import { stripHtml } from 'string-strip-html';
import striptags from 'striptags';
import UIkit from 'uikit';

const emptyArray = [];

const HelperFunctions = {
    addToAllVariants(allVariants, variant) {
        allVariants.push({
            id: variant.id,
            name: variant.name,
            prefix: variant.prefix,
            parentId: variant.parentId,
            sortOrder: variant.sortOrder,
            variantGroup: variant.variantGroup,
            enabled: variant.enabled,
        });

        if (variant.children.length > 0) {
            variant.children.map((variant) => {
                return this.addToAllVariants(allVariants, variant);
            });
        }

        return allVariants;
    },
    arrayMove(array, fromIndex, toIndex) {
        return arrayMoveImmutable(array, fromIndex, toIndex);
    },
    arrayInsert(array, item, index) {
        return [...array.slice(0, index), item, ...array.slice(index)];
    },
    arrayToggle(array, value) {
        return _.xor(array, [value]);
    },
    changesSaved() {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 2000);
        });
    },
    checkRoleOrPermission(role, requiredRoles) {
        // required role can have the form of an array including roles that have permission
        // and also of an object containing a single key 'not' with an array including roles that do not have permission
        if (role === undefined || requiredRoles === undefined) return false;

        if (requiredRoles.hasOwnProperty('not')) {
            return requiredRoles.not.indexOf(role) < 0;
        }

        try {
            return requiredRoles.indexOf(role) >= 0;
        } catch (e) {
            return false;
        }
    },
    clone(obj) {
        if (null == obj || 'object' != typeof obj) return obj;
        let copy = obj.constructor();
        for (let attr in obj) {
            if (obj.hasOwnProperty(attr)) copy[attr] = obj[attr];
        }
        return copy;
    },
    createTagElement(tag, title, tooltip = 'Kruisverwijzing') {
        return `<span data-uk-tooltip="${tooltip}" class="refs-button" data-replace="${_.trim(tag, '[]')}">
<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-shuffle mr-1" viewBox="0 0 16 16">
  <path fill-rule="evenodd" d="M0 3.5A.5.5 0 0 1 .5 3H1c2.202 0 3.827 1.24 4.874 2.418.49.552.865 1.102 1.126 1.532.26-.43.636-.98 1.126-1.532C9.173 4.24 10.798 3 13 3v1c-1.798 0-3.173 1.01-4.126 2.082A9.6 9.6 0 0 0 7.556 8a9.6 9.6 0 0 0 1.317 1.918C9.828 10.99 11.204 12 13 12v1c-2.202 0-3.827-1.24-4.874-2.418A10.6 10.6 0 0 1 7 9.05c-.26.43-.636.98-1.126 1.532C4.827 11.76 3.202 13 1 13H.5a.5.5 0 0 1 0-1H1c1.798 0 3.173-1.01 4.126-2.082A9.6 9.6 0 0 0 6.444 8a9.6 9.6 0 0 0-1.317-1.918C4.172 5.01 2.796 4 1 4H.5a.5.5 0 0 1-.5-.5"/>
  <path d="M13 5.466V1.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384l-2.36 1.966a.25.25 0 0 1-.41-.192m0 9v-3.932a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384l-2.36 1.966a.25.25 0 0 1-.41-.192"/>
</svg>${title}</span>`;
    },
    csvToArray(text = '') {
        let p = '',
            row = [''],
            ret = [row],
            i = 0,
            r = 0,
            s = !0,
            l;
        for (l of text) {
            if ('"' === l) {
                if (s && l === p) row[i] += l;
                s = !s;
            } else if (',' === l && s) l = row[++i] = '';
            else if ('\n' === l && s) {
                if ('\r' === p) row[i] = row[i].slice(0, -1);
                row = ret[++r] = [(l = '')];
                i = 0;
            } else row[i] += l;
            p = l;
        }
        return ret;
    },
    alertModal(message) {
        return UIkit.modal.alert(striptags(message));
    },
    confirmModal(translation, okLabel = '', cancelLabel = '', type = 'default', extra = '') {
        return new Promise((resolve, reject) => {
            const message = `<div class="message">${striptags(translation)}</div>`;
            const extraMessage = extra !== '' ? `<div class="extra">${extra}</div>` : '';

            UIkit.modal
                .confirm(message + extraMessage, {
                    i18n: {
                        ok: okLabel,
                        cancel: cancelLabel,
                    },
                    clsPage: `uk-modal-page uk-modal-${type}`,
                })
                .then(
                    () => resolve(),
                    () => reject(),
                );
        });
    },
    dynamicSort(property, sortOrder = 1) {
        if (property[0] === '-') {
            sortOrder = -1;
            property = property.substr(1);
        }
        return function (a, b) {
            const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
            return result * sortOrder;
        };
    },
    emailIsValid(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    sortByString(property, direction = 'ASC') {
        if (direction === 'ASC') {
            return (a, b) => a[property].localeCompare(b[property], undefined, { sensitivity: 'base' });
        }

        return (a, b) => b[property].localeCompare(a[property], undefined, { sensitivity: 'base' });
    },
    filterNumbers(value) {
        return value.match(/\d+/g).join([]);
    },
    findInDocument(ref, sections = []) {
        if (ref.type === 'section') {
            return this.getByValue(sections, 'id', parseInt(ref.id));
        } else if (ref.type === 'area') {
            return this.findArea(parseInt(ref.id), sections);
        } else if (ref.type === 'block') {
            const area = this.findArea(parseInt(ref.areaId), sections);

            if (area) {
                return {
                    title: `Block #${ref.id} - ${area.title}`,
                };
            }
        }

        return undefined;
    },
    findArea(areaId, sections = []) {
        for (const section of sections) {
            // Check if the section has an 'areas' property and it is an array
            if (section.areas && Array.isArray(section.areas)) {
                for (const area of section.areas) {
                    if (area.id === areaId) {
                        return area;
                    }
                }
            }
        }

        return undefined;
    },
    findTranslation(entity, locale, field) {
        if (locale === Constants.language.defaultLanguage) {
            return entity.hasOwnProperty(field) ? entity[field] : '';
        }

        if (entity.hasOwnProperty('translations') === false || entity.translations.length === 0) {
            return '';
        }

        const translation = this.findTranslationInArray(entity.translations, locale, field);

        return translation ? translation['content'] : '';
    },
    findTranslationInArray(arr, locale, field) {
        return arr.find((translation) => {
            return translation['locale'] === locale && translation['field'] === field;
        });
    },
    flatten(collection = [], flattened = [], childPropertyName = 'children') {
        _.each(collection, (obj) => {
            flattened.push(obj);
            this.flatten(obj[childPropertyName], flattened, childPropertyName);
        });

        return flattened;
    },
    formIsValid(formData, requiredFields) {
        if (requiredFields.length === 0) {
            return true;
        }

        let formIsValid = true;

        requiredFields.forEach((field) => {
            if (formData.hasOwnProperty(field) && formData[field].length < 1) {
                formIsValid = false;
            }
        });

        return formIsValid;
    },
    getByValue(arr, attr, value) {
        for (let i = 0, iLen = arr.length; i < iLen; i++) {
            if (arr[i][attr] === value) return arr[i];
        }
    },
    getByValueRecursive(arr, value, attr = 'id', childProp = 'children') {
        if (arr) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i][attr] === value) {
                    return arr[i];
                }
                const found = this.getByValueRecursive(arr[i][childProp], value, attr, childProp);
                if (found) return found;
            }
        }
    },
    getLocalDateTime(timestamp) {
        const dateObj = new Date(timestamp * 1000);
        return (
            String(dateObj.getDate()).padStart(2, '0') +
            '-' +
            String(dateObj.getMonth() + 1).padStart(2, '0') +
            '-' +
            dateObj.getFullYear() +
            ' ' +
            dateObj.getHours() +
            ':' +
            String(dateObj.getMinutes()).padStart(2, '0')
        );
    },
    getTime(timestamp) {
        // expects timestamp in seconds
        const dateObj = new Date(timestamp * 1000);
        return dateObj.getHours() + ':' + String(dateObj.getMinutes()).padStart(2, '0');
    },
    getTimestampNow() {
        // timestamp will be in seconds, not miliseconds
        const date = new Date();
        return Math.floor(date.getTime() / 1000);
    },
    getUniversalDate(timestamp) {
        // timestamp must be in seconds, not miliseconds!
        if (timestamp === undefined) {
            // use 'now'
            timestamp = this.getTimestampNow();
        }
        let dateObj = new Date(timestamp * 1000);
        let dateString =
            dateObj.getFullYear() +
            '-' +
            ('0' + (dateObj.getMonth() + 1)).slice(-2) +
            '-' +
            ('0' + dateObj.getDate()).slice(-2);
        return dateString;
    },
    formatTimestampForInput(text) {
        return DateTime.fromISO(text).toISO().slice(0, 16);
    },
    isEmpty(obj) {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) return false;
        }
        return true;
    },
    empty(value) {
        return value == null || value.length === 0;
    },
    isLastInArray(items = [], object = {}) {
        const lastItem = items.slice(-1)[0];
        return lastItem.id === object.id;
    },
    moveItem(itemId, items, direction, sortProperty = 'sortOrder') {
        // Make sure the items are sorted
        const itemsSorted = [...items].sort(this.dynamicSort(sortProperty));
        const itemIndex = itemsSorted.findIndex((item) => item.id === itemId);

        let swapIndex = -1;
        if (direction === 'up' && itemIndex > 0) {
            swapIndex = itemIndex - 1;
        } else if (direction === 'down' && itemIndex < itemsSorted.length - 1) {
            swapIndex = itemIndex + 1;
        }

        if (swapIndex >= 0) {
            const item = {
                ...itemsSorted[itemIndex],
                [sortProperty]: itemsSorted[swapIndex][sortProperty],
            };
            const swapItem = {
                ...itemsSorted[swapIndex],
                [sortProperty]: itemsSorted[itemIndex][sortProperty],
            };

            return { items, updated: [item, swapItem] };
        } else {
            return false;
        }
    },
    prepareDropdownData(items, labelKey = 'name', valueKey = 'id') {
        if (items !== undefined && items.length > 0) {
            return items.map((item) => {
                return {
                    ...item,
                    label: item[labelKey],
                    value: item[valueKey],
                };
            });
        }
        return [];
    },
    prepareOptionValues(options = [], nameProp = 'name', prefixProp = 'prefix') {
        return mapValuesDeep(options, (value, key, parentValue) => {
            if (key === 'label') {
                return <LabelWithPrefix name={_.get(parentValue, nameProp)} prefix={_.get(parentValue, prefixProp)} />;
            }

            return value;
        });
    },
    prepareQuery(url, params = {}) {
        if (_.isEmpty(params)) {
            return url;
        }

        return queryString.stringifyUrl({ url, query: params }, { arrayFormat: 'bracket' });
    },
    prepareRefs(text, sections = []) {
        return text.replace(/\[Ref (.*?)]/gm, (match) => {
            const ref = {
                id: 0,
                document: 0,
                type: '',
            };

            // Replace html entity
            const rawTag = match;
            let tag = rawTag.replace(/&#61;/g, '=');

            // Remove start and end part of [Tag ... ]
            tag = tag.slice(5).slice(0, -1);

            // Loop string for params
            tag.split(' ').forEach((params) => {
                const param = params.split('=');
                const key = param[0];

                ref[key] = param[1];
            });

            // Check if id is set
            if (_.isEmpty(ref.id)) {
                // Parse failed
                return rawTag;
            }

            // Find entity in Document
            const object = this.findInDocument(ref, sections);
            const title = object === undefined ? 'Reference not found' : `${object.title}`;

            return HelperFunctions.createTagElement(rawTag, title);
        });
    },

    /**
     * @param text
     * @param document
     * @param useHtmlTag
     * @param allowQuickEdit
     * @param sections
     * @param {string[]} tags
     */
    prepareTags(text, document = undefined, useHtmlTag, allowQuickEdit = false, sections = [], tags = []) {
        if (text === undefined || text === null) {
            return false;
        }

        if (document === undefined) {
            return text;
        }

        text = this.prepareTooltipTags(text);
        text = this.prepareTagChangedValues(text);
        text = this.prepareRefs(text, sections);

        if (tags.length === 0) {
            return text;
        }

        // A tag can start with "[Tag" or only "["
        const regex = /\[(Tag |)(.*?)\]/gm;

        text = text.replace(regex, (match) => {
            const shortTag = !match.startsWith('[Tag ');
            const tagName = match.slice(shortTag ? 1 : 5, match.length - 1);
            const tagExists = tags.includes(tagName);

            if (!tagExists) {
                return match;
            }

            let tagText = '[' + tagName + ']';
            let tagClass = 'fixed-link';

            if (allowQuickEdit) {
                return `<button type="button" class="tags-button" data-tag="${tagName}">test</button>`;
            }

            return shortTag && (useHtmlTag === undefined || !useHtmlTag)
                ? tagText
                : '<span class="' + tagClass + '" data-tag="' + tagName + '">' + tagText + '</span>';
        });

        return text;
    },

    /**
     * Formats the ugly [Tooltip text= value=] into a little icon that shows the tooltip when hovered over.
     * @param text
     * @returns {boolean|*}
     */
    prepareTooltipTags(text) {
        if (text === undefined || text === null) {
            return false;
        }

        // A tag tooltip definition starts with "[Tooltip"
        const regex = /\[Tooltip(.*?)\]/gm;

        text = text.replace(regex, (match) => {
            const parts = match.split('value=');

            if (parts.length === 2) {
                const tooltipValue = stripHtml(this.phpUrldecode(parts[1].slice(0, -1)).trim());
                const tooltip = tooltipValue.result.replaceAll("'", '"').replaceAll('‘', '"').replaceAll('’', '"');
                return `<sup data-uk-tooltip='title: ${tooltip}'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-question-circle" viewBox="0 0 16 16">
                              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                              <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286m1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94"/>
                            </svg>
                    </sup>`;
            }

            return match;
        });

        return text;
    },

    /**
     * Formats the string [~~ChangedValues= ~~] into a little icon that shows the changed values when hovering over.
     *
     * @param text
     * @returns {boolean|*}
     */
    prepareTagChangedValues(text) {
        if (text === undefined || text === null) {
            return false;
        }

        // If a tag has history included the format is "[~~ChangedValues=XYZ~~]"
        const regex = /\[~~ChangedValues=(.*?)~~\]/gms;

        text = text.replace(regex, (_, groupedValue) => {
            const tooltipContent =
                `<span>` +
                `<div class='tooltip-banner'><span class='tooltip-title'>Wijzigingen in deze revisieronde</span></div>` +
                `<div class='inner-tooltip'>${groupedValue}</div>` +
                `</span>`;

            // Auto-formatting might break this (convert " to ') so just find/replace them
            return `<sup data-uk-tooltip='title: $$tooltipContent$$; cls: uk-active mark-changes'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-question-circle" viewBox="0 0 16 16">
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                          <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286m1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94"/>
                        </svg>
                    </sup>`
                .replace(/'/g, '"')
                .replace('$$tooltipContent$$', tooltipContent);
        });

        return text;
    },

    /**
     * Implementation to decode a string that was encoded by urlEncode in php.
     *
     * @param url
     * @returns {string}
     */
    phpUrldecode(url) {
        return decodeURIComponent(url.replace(/\+/g, ' '));
    },
    queryResult(result, hydra = true) {
        const { currentData, isUninitialized, isLoading, isFetching } = result;
        const items = currentData ? currentData[hydra ? 'hydra:member' : 'member'] : emptyArray;

        return {
            ...result,
            items,
            totalItems: currentData ? currentData[hydra ? 'hydra:totalItems' : 'totalItems'] : 0,
            isEmpty: items.length === 0 && !isUninitialized && !isLoading && !isFetching,
            isLoading: isUninitialized || (isFetching && items.length === 0),
        };
    },
    removeSpaces(string = '') {
        return string.replace(/[^a-z0-9]/gi, '');
    },
    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    },
    slugify(string = '', allowForwardSlash = false) {
        if (allowForwardSlash) {
            return slugify(string, {
                lower: true,
                strict: false,
                trim: false,
                remove: /[^\w\s$*_+~.()'"!\-:@/]+/g,
            });
        }

        return slugify(string, {
            lower: true,
            strict: true,
            trim: false,
        });
    },
    tryParseJSON(jsonString, defaultValue = false) {
        try {
            const o = JSON.parse(jsonString);

            // Handle non-exception-throwing cases:
            // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
            // but... JSON.parse(null) returns null, and typeof null === "object",
            // so we must check for that, too. Thankfully, null is falsey, so this suffices:
            if (o && typeof o === 'object') {
                return o;
            }
        } catch (e) {}

        return defaultValue;
    },
    upsert(array, element, property = 'id') {
        const newArray = [...array];

        const i = newArray.findIndex((_element) => _.get(_element, property) === _.get(element, property));
        if (i > -1) newArray[i] = element;
        else newArray.push(element);

        return newArray;
    },
    variantAsList(variant, list = [], depth = 0) {
        list.push({
            ...variant,
            label: variant.name,
            value: variant.id,
            isDisabled: variant.variantGroup || !variant.enabled,
        });

        if (variant.children.length > 0) {
            variant.children.forEach((childVariant) => {
                this.variantAsList(childVariant, list, depth + 1);
            });
        }

        return list;
    },
};

export default HelperFunctions;
